/**
 * Tiny self-synthesised voice for living creatures (WebAudio, no files): boing, pop, giggle, whirl,
 * wake-up. Respects Settings → Sound effects. Kept local to the living module.
 */
import type { CreatureAction } from '@sketchling/core';
import { useApp } from '../../state/store';

let ctx: AudioContext | null = null;
function ac(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!useApp.getState().settings.sfx) return null;
  const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!C) return null;
  try {
    ctx ??= new C();
    if (ctx.state === 'suspended') void ctx.resume();
  } catch {
    return null;
  }
  return ctx;
}

function blip(c: AudioContext, at: number, f0: number, f1: number, dur: number, type: OscillatorType = 'sine', gain = 0.12) {
  const o = c.createOscillator();
  const g = c.createGain();
  const t0 = c.currentTime + at;
  o.type = type;
  o.frequency.setValueAtTime(f0, t0);
  o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(c.destination);
  o.start(t0);
  o.stop(t0 + dur + 0.05);
}

export type LivingSound = 'boing' | 'pop' | 'giggle' | 'whirl' | 'wake' | 'shimmer';

export function livingSound(s: LivingSound, pitch = 1): void {
  const c = ac();
  if (!c) return;
  const p = pitch;
  switch (s) {
    case 'boing':
      blip(c, 0, 180 * p, 520 * p, 0.22, 'triangle', 0.14);
      blip(c, 0.2, 520 * p, 260 * p, 0.28, 'sine', 0.09);
      break;
    case 'pop':
      blip(c, 0, 900 * p, 300 * p, 0.09, 'sine', 0.14);
      break;
    case 'giggle':
      for (let i = 0; i < 4; i++) blip(c, i * 0.075, (700 + (i % 2) * 160) * p, (820 + (i % 2) * 160) * p, 0.06, 'sine', 0.07);
      break;
    case 'whirl':
      blip(c, 0, 300 * p, 1200 * p, 0.42, 'sine', 0.08);
      blip(c, 0.05, 450 * p, 1500 * p, 0.36, 'triangle', 0.04);
      break;
    case 'wake':
      [523, 659, 784, 1047].forEach((f, i) => blip(c, i * 0.09, f * p, f * p * 1.01, 0.28, 'sine', 0.08));
      blip(c, 0.45, 260 * p, 640 * p, 0.25, 'triangle', 0.1);
      break;
    case 'shimmer':
      for (let i = 0; i < 7; i++) blip(c, i * 0.05, (1400 + i * 180) * p, (1500 + i * 180) * p, 0.16, 'sine', 0.035);
      break;
  }
}

export const ACTION_SOUND: Record<CreatureAction, LivingSound> = {
  intro: 'wake',
  jump: 'boing',
  wave: 'giggle',
  twirl: 'whirl',
  wiggle: 'giggle',
  walk: 'pop',
};
